<template>
  <ListView
    :title="computedTitle"
    :isFluid="true"
    @reload="reload"
    @create="actionCreate"
    @navDetails="actionDetails"
    :enable-create="true"
    :search.sync="search"
    :data="data"
    :page.sync="page"
    :header="header"
    :search-box-placeholder="searchBoxPlaceholder"
    :enable-reload="memberId != null"
  >
    <template v-slot:filter>
      <div class="d-flex align-center mb-8 flex-gap">
        <div style="width: 15rem" v-if="memberId === null">
          <FletrixAutocomplete
            label="Clinic"
            placeholder="Auto complete"
            path="rml/admin/ClinicProfiles"
            :params="{
              sort: { created_at: -1 },
            }"
            item-value="_id"
            v-model="selectedClinic"
            :clearable="true"
            ref="autoComplete"
            solo
          >
            <template v-slot:item="data">
              <span>{{ data.item.name | parseAndLocalize('en') }}</span>
            </template>
            <template v-slot:selection="data">
              <span>{{ data.item.name | parseAndLocalize('en') }}</span>
            </template>
          </FletrixAutocomplete>
        </div>
        <div style="width: 12rem" v-if="memberId === null">
          <fletrix-date-picker
            v-model="bookingStartDate"
            label="Booking Date Start"
            placeholder="Select"
            hide-details="auto"
            solo
            clearable
          />
        </div>
        <div style="width: 12rem">
          <fletrix-date-picker
            v-model="bookingEndDate"
            label="Booking Date End"
            placeholder="Select"
            hide-details="auto"
            solo
            clearable
          />
        </div>
        <div style="width: 12rem">
          <fletrix-date-picker
            v-model="startDate"
            label="Created Date Start"
            placeholder="Select"
            hide-details="auto"
            solo
            clearable
          />
        </div>
        <div style="width: 12rem">
          <fletrix-date-picker
            v-model="endDate"
            label="Created Date End"
            placeholder="Select"
            hide-details="auto"
            solo
          />
        </div>
        <div style="width: 12rem">
          <fletrix-select
            v-model="selectedStatus"
            :items="bookingStatusOptions"
            item-text="label"
            item-value="value"
            label="Booking Status"
            hide-details="auto"
            solo
          />
        </div>
        <div style="width: 12rem">
          <fletrix-select
            v-model="selectedTimeTableStatus"
            :items="timeTableStatusOptions"
            item-text="label"
            item-value="value"
            label="Time Table Status"
            hide-details="auto"
            solo
          />
        </div>
      </div>
    </template>
    <template v-slot:item.clinic="{ item }">
      {{ item.clinic.name | parseAndLocalize('en') }}
    </template>
    <template v-slot:item._member="{ item }">
      <div>{{ item.member.name | parseAndLocalize('en') }}</div>
      <div>{{ item.member.phone | displayPhone }}</div>
    </template>
    <template v-slot:item._services="{ item }">
      {{ item.serviceDetails.service.name | parseAndLocalize('en') }}
    </template>
    <template v-slot:item._patient="{ item }">
      {{ item.serviceDetails.patient.name_first }} {{ item.serviceDetails.patient.name_last }}
    </template>
    <template v-slot:item._status="{ item }">
      <span :style="{ color: getColor(item.status) }" label>
        <template v-if="item.approvalStatus === bookingApprovalStatus.pending">Pending</template>
        <template v-else-if="item.status === bookingStatus.cancelled">
          <template v-if="item.cancelledByType === 'clinic'">Cancelled by Clinic</template>
          <template v-else-if="item.cancelledByType === 'member'">Cancelled by Patient</template>
          <template v-else>Cancelled</template>
        </template>
        <template v-else-if="item.status === bookingStatus.completed">Completed</template>
        <template v-else-if="item.approvalStatus === bookingApprovalStatus.approved"
          >Approved</template
        >
        <template v-else-if="item.isExpired === true"
          ><span style="color: red">Expired</span></template
        >
      </span>
      <span class="voided-booking"> {{ item.isVoided ? '(Voided)' : '' }}</span>
    </template>
    <template v-slot:item._timetable.status="{ item }">
      <template v-if="item.approvalStatus === bookingApprovalStatus.pending">
        Pending <span style="color: red">{{ item.isExpired ? '(Expired)' : '' }}</span>
      </template>
      <template v-else-if="item.status === bookingStatus.arrived"
        >Arrived <span style="color: red">{{ item.isExpired ? '(Expired)' : '' }}</span></template
      >
      <template v-else-if="item.status === bookingStatus.absent">Absent</template>
      <template v-else-if="item.approvalStatus === bookingApprovalStatus.approved">
        Approved <span style="color: red">{{ item.isExpired ? '(Expired)' : '' }}</span>
      </template>
      <template v-else-if="item.status === bookingStatus.completed">Completed</template>
      <template v-else>-</template>
    </template>
    <template v-slot:item.created_at="{ item }">
      {{ item.created_at | moment('DD-MMM-YYYY') }}
    </template>
    <template v-slot:item._timetable.time="{ item }">
      {{ item.time && item.time | datetimeRange }}
    </template>
    <template v-slot:item.serviceDetails.masterSpecified.name="{ item }">
      {{ (item.serviceDetails.masterSpecified && item.serviceDetails.masterSpecified.name) || '-' }}
    </template>
    <BookingDetailsDialog
      v-model="showBookingDetailsDialog"
      :id="selectedBookingId"
      :booking="loadingTimetable ? null : selectedBookingData"
      :timetableData="selectedTimetableData"
      @create="handleBookingUpdate"
      @update="handleBookingUpdate"
      @cancel="handleBookingCancel"
      @close="onBookingDetailsDialogClose"
    />

    <BookingEditDialog
      v-model="editDialog"
      :id="editId"
      :title="editDialogTitle"
      :memberId="memberId"
      @create="reload"
      @update="reload"
      @delete="reload"
    />
  </ListView>
</template>

<script>
import { bookingSortOption, bookingStatusOption, month, years } from '@/data/optionsConstants';
import listViewPageMixin from '@/services/listViewPageMixin';
import BookingDetailsDialog from '@/components/Booking/BookingDetailsDialog.vue';
import BookingEditDialog from '@/components/Booking/BookingEditDialog.vue';
import { mapGetters } from 'vuex';
import { RESTFUL, bookingStatus, bookingApprovalStatus } from '@/data/constants';
import { readTimeTableByBooking } from '@/api/timetable';
import api from '@/api';
import moment from 'moment';
import { get } from 'lodash';
import { FletrixDatePicker, FletrixSelect, FletrixAutocomplete } from '@/components/Atom';
export default {
  name: 'BookingList',

  components: {
    BookingDetailsDialog,
    BookingEditDialog,
    FletrixDatePicker,
    FletrixSelect,
    FletrixAutocomplete,
  },
  props: {
    memberId: {
      type: String,
      default: null,
    },
    listTitle: {
      type: String,
      default: 'Bookings',
    },
  },
  mixins: [listViewPageMixin],
  data() {
    const today = moment().format('YYYY-MM-DD');
    return {
      bookingStatus,
      bookingApprovalStatus,
      selectedClinic: null,
      bookingStartDate: today,
      bookingEndDate: today,
      startDate: null,
      endDate: null,
      selectedStatus: {},
      selectedTimeTableStatus: {},
      populate: [{ path: 'clinic' }, { path: 'member' }],
      sort: { created_at: -1 },
      filterStatus: null,
      bookingSortOption,
      dialogTitle: 'Booking Details',
      showBookingDetailsDialog: false,
      selectedBookingId: null,
      selectedBookingData: null,
      selectedTimetableData: null,
      loadingTimetable: false,
      selectedRole: null,
      editDialog: false,
      editId: null,
      editDialogTitle: 'Booking',
      searchBoxPlaceholder: 'Search by Patient Name/ Patient Phone No.',
    };
  },
  computed: {
    ...mapGetters('auth', ['role', 'isClinicStaff', 'clinicId']),
    computedTitle() {
      return this.listTitle;
    },
    restfulURL() {
      return RESTFUL.booking.list;
    },
    bookingStatusOption: () => bookingStatusOption,
    bookingStatusOptions() {
      return [
        { value: {}, label: 'All' },
        { value: { approvalStatus: bookingApprovalStatus.pending }, label: 'Pending' },
        {
          value: {
            approvalStatus: bookingApprovalStatus.approved,
            status: { $ne: bookingStatus.cancelled },
          },
          label: 'Approved',
        },
        // { value: { approvalStatus: bookingApprovalStatus.unapproved }, label: 'Unapproved' },
        {
          value: { status: bookingStatus.cancelled, cancelledByType: 'clinic' },
          label: 'Cancelled By Clinic',
        },
        {
          value: { status: bookingStatus.cancelled, cancelledByType: 'member' },
          label: 'Cancelled By Patient',
        },
        // {
        //   value: { status: bookingStatus.completed },
        //   label: 'Completed',
        // },
        {
          value: { status: bookingStatus.absent },
          label: 'Absent',
        },
      ];
    },
    timeTableStatusOptions() {
      return [
        { value: {}, label: 'All' },
        {
          value: { approvalStatus: bookingApprovalStatus.pending, isExpired: { $ne: true } },
          label: 'Pending',
        },
        {
          value: { approvalStatus: bookingApprovalStatus.pending, isExpired: true },
          label: 'Pending (Expired)',
        },
        {
          value: { status: bookingStatus.arrived, isExpired: { $ne: true } },
          label: 'Arrived',
        },
        {
          value: { status: bookingStatus.arrived, isExpired: true },
          label: 'Arrived (Expired)',
        },
        {
          value: { approvalStatus: bookingApprovalStatus.approved, isExpired: { $ne: true } },
          label: 'Approved',
        },
        {
          value: { approvalStatus: bookingApprovalStatus.approved, isExpired: true },
          label: 'Approved (Expired)',
        },
        { value: { approvalStatus: bookingStatus.absent }, label: 'Absent' },
        { value: { approvalStatus: bookingStatus.completed }, label: 'Completed' },
      ];
    },
    filter() {
      const filters = {};

      // Existing filters
      if (this.filterStatus) {
        filters.status = this.filterStatus;
      }

      // New filters
      if (this.selectedClinic) {
        filters.clinic = this.selectedClinic;
      }

      // Special booking status filter with complex objects
      if (this.selectedStatus && Object.keys(this.selectedStatus).length > 0) {
        // Merge the selected status filter object into filters
        Object.assign(filters, this.selectedStatus);
      }

      if (this.selectedTimeTableStatus && Object.keys(this.selectedTimeTableStatus).length > 0) {
        // Merge the selected status filter object into filters
        Object.assign(filters, this.selectedTimeTableStatus);
      }

      // Booking date range filter
      // if memberId is not provided, then filter by booking date range
      if (!this.memberId && (this.bookingStartDate || this.bookingEndDate)) {
        if (this.bookingStartDate) {
          filters['time.start'] = { $gte: moment(this.bookingStartDate).startOf('day').toDate() };
        }
        if (this.bookingEndDate) {
          filters['time.end'] = { $lte: moment(this.bookingEndDate).endOf('day').toDate() };
        }
      }

      // Created date range filter
      if (this.startDate || this.endDate) {
        filters.created_at = {};
        if (this.startDate) {
          filters.created_at.$gte = moment(this.startDate).startOf('day').toDate();
        }
        if (this.endDate) {
          filters.created_at.$lte = moment(this.endDate).endOf('day').toDate();
        }
      }

      // If memberId is provided, filter by member
      if (this.memberId) {
        filters.member = this.memberId;
      }
      // If clinic staff, filter by their clinic
      if (this.isClinicStaff && this.clinicId) {
        filters.clinic = this.clinicId;
      }
      return Object.keys(filters).length > 0 ? filters : null;
    },
    header() {
      // Hide clinic column for clinic staff
      const baseHeaders = [
        { text: 'Clinic', value: 'clinic', width: '150px' },
        { text: 'Created Date', value: 'created_at', width: '150px' },
        { text: 'Booking Account', value: '_member', width: '150px' },
        { text: 'Patient Name', value: '_patient', width: '150px' },
        { text: 'Booking Time', value: '_timetable.time' },
        {
          text: 'Services',
          value: '_services',
        },
        { text: 'Booking Status', value: '_status' },
        { text: 'Time Table Status', value: '_timetable.status' },
      ];

      if (this.isClinicStaff) {
        // Remove clinic column for clinic staff
        return baseHeaders.filter(h => h.value !== 'clinic');
      }
      return baseHeaders;
    },
  },
  methods: {
    get,
    getColor(status) {
      if (status === '97$cancelled' || status === '98$absent') return 'red';
      // else return 'green'
    },
    async actionDetails(item) {
      // Immediately show the dialog with booking data
      this.selectedBookingId = item._id;
      this.selectedBookingData = item;
      this.selectedTimetableData = null;
      this.loadingTimetable = true;
      this.showBookingDetailsDialog = true;

      // Fetch timetable data in the background
      try {
        const timetableData = await readTimeTableByBooking(api, item._id);
        this.selectedTimetableData = timetableData;
      } catch (error) {
        // If no timetable exists, that's okay - some bookings may not have timetables yet
        console.log('No timetable found for booking:', item._id);
        this.selectedTimetableData = null;
      } finally {
        this.loadingTimetable = false;
      }
    },

    handleBookingUpdate(booking) {
      // Handle booking update
      this.reload();
      //this.onBookingDetailsDialogClose();
    },

    handleBookingCancel(booking) {
      // Handle booking cancellation
      this.reload();
      this.onBookingDetailsDialogClose();
    },

    onBookingDetailsDialogClose() {
      // Clear selected booking data when dialog closes
      this.selectedBookingId = null;
      this.selectedBookingData = null;
      this.selectedTimetableData = null;
      this.loadingTimetable = false;
      this.showBookingDetailsDialog = false;
    },

    actionCreate() {
      // Open the BookingEditDialog for creating a new booking
      this.editId = 'new';
      this.editDialogTitle = 'Create New Booking';
      this.editDialog = true;
    },
  },
};
</script>

<style scoped lang="scss">
.voided-booking {
  color: red;
}
</style>
